import config from "../config/pyconfig";

export const CX_API_SHAPES = {
  reorderBodyKey: "question_ids", // PUT /order  → { question_ids: ["q_..", "q_.."], expected_version: 15 }
  exportFormatParam: "format",    // GET /export?format=docx | pdf
  exportFormats: { word: "docx", pdf: "pdf" },
};

const base = (userId, setId) =>
  `${config.apiBaseUrl}/cross-exam/${encodeURIComponent(userId)}/sets${
    setId ? `/${encodeURIComponent(setId)}` : ""
  }`;

async function request(url, { method = "GET", body } = {}) {
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = "";
    try {
      const j = await res.json();
      detail = typeof j?.detail === "string" ? j.detail : JSON.stringify(j?.detail ?? j);
    } catch (_) { /* no body */ }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const type = res.headers.get("content-type") || "";
  return type.includes("application/json") ? res.json() : res.text();
}

/* 1. GET  /cross-exam/{user_id}/sets                      — List Sets */
export const listSets = (userId) => request(base(userId));

/* 2. GET  /cross-exam/{user_id}/sets/{set_id}             — Get Set */
export const getSet = (userId, setId) => request(base(userId, setId));

/* Optimistic locking — set-oda current version. Server match aagalana 409 → panel retry pannum. */
const withVersion = (body, expectedVersion) =>
  Number.isInteger(expectedVersion) ? { ...body, expected_version: expectedVersion } : body;

/* 3. PATCH /cross-exam/{user_id}/sets/{set_id}            — Edit Set
   FLAT body: { case_name?, case_number?, witness_name?, side?, objective?, status?, expected_version } */
export const editSet = (userId, setId, patch, expectedVersion) =>
  request(base(userId, setId), { method: "PATCH", body: withVersion(patch, expectedVersion) });

/* 4. POST /cross-exam/{user_id}/sets/{set_id}/questions   — Add Question */
export const addQuestion = (userId, setId, question = {}, expectedVersion) =>
  request(`${base(userId, setId)}/questions`, {
    method: "POST",
    body: withVersion(question, expectedVersion),
  });

/* 5. PATCH /cross-exam/{user_id}/sets/{set_id}/questions/{question_id} — Edit Question
   patch: any of { category, question, purpose, strategy, leading, follow_ups, advocate_note, reviewed } */
export const editQuestion = (userId, setId, questionId, patch, expectedVersion) =>
  request(`${base(userId, setId)}/questions/${encodeURIComponent(questionId)}`, {
    method: "PATCH",
    body: withVersion(patch, expectedVersion),
  });

/* 6. DELETE /cross-exam/{user_id}/sets/{set_id}/questions/{question_id} — Delete Question
   DELETE-ku body illa → expected_version query param-a */
export const deleteQuestion = (userId, setId, questionId, expectedVersion) =>
  request(
    `${base(userId, setId)}/questions/${encodeURIComponent(questionId)}${
      Number.isInteger(expectedVersion) ? `?expected_version=${expectedVersion}` : ""
    }`,
    { method: "DELETE" }
  );

/* 7. PUT /cross-exam/{user_id}/sets/{set_id}/order        — Reorder */
export const reorderQuestions = (userId, setId, questionIds, expectedVersion) =>
  request(`${base(userId, setId)}/order`, {
    method: "PUT",
    body: withVersion({ [CX_API_SHAPES.reorderBodyKey]: questionIds }, expectedVersion),
  });

/* 8. GET /cross-exam/{user_id}/sets/{set_id}/history      — History */
export const getHistory = (userId, setId) => request(`${base(userId, setId)}/history`);

/* 9. GET /cross-exam/{user_id}/sets/{set_id}/export       — Export (Word / PDF download) */
export async function exportSet(userId, setId, kind = "word", fileTitle = "cross-examination") {
  const fmt = CX_API_SHAPES.exportFormats[kind] || kind;
  const url = `${base(userId, setId)}/export?${CX_API_SHAPES.exportFormatParam}=${encodeURIComponent(fmt)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();


  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const safeTitle = fileTitle.replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "_") || "cross-examination";
  const filename = m ? decodeURIComponent(m[1]) : `${safeTitle}.${fmt}`;

  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1500);
}

export function parseCrossExamEvent(payload, fallbackUserId) {
  const setId = payload?.set_id;
  const m = typeof payload?.api === "string" && payload.api.match(/\/cross-exam\/([^/]+)\/sets\/([^/?#]+)/);
  return {
    setId: setId || (m ? m[2] : null),
    userId: (m && m[1]) || fallbackUserId,
  };
}

export function findSetIdInText(text = "") {
  const m = typeof text === "string" && text.match(/question set\s+`?(cx_[a-z0-9]+)`?/i);
  return m ? m[1] : null;
}